import { useContext } from 'react'
import { PassContext } from "../../pass-context"
import dataSelector from '../data-selector'
import fieldFormatter from '../fronts/components/field-formatter.js'
import parseHtml from 'html-react-parser'
import sanitize from 'sanitize-html'

const styles = {
  child: {
    padding: 15,
    fontSize: 17
  },
  lastChild: {
    borderBottom: "1px solid #ddd",
    padding: 15,
    fontSize: 17
  }
}

// If content and label can fit on one line then do so.
// If not, then label on one line, content follows...
const Field = ({ field, isLastField }) => {
  const { variables } = useContext(PassContext)

  const resolvedField = dataSelector({ template: { field }, variables, path: "field" })

  const rawLabel = resolvedField.label ? cleanHtml(resolvedField.label, []) : ""
  const resolvedValue = fieldFormatter({ field, value: resolvedField.value })

  const { value, label } = addDataFormatting(resolvedField.type, resolvedValue, rawLabel)

  const key = field.key

  // Different formatting if we have a label to when we do not
  if (label.length) return fieldWithLabel({ key, label, value, isLastField })
  if (!label.length) return fieldWithoutLabel({ key, value, isLastField })

}

export default Field

const fieldWithLabel = ({ key, label, value, isLastField }) => {
  let length = (label + value).length

  // 30 seems to be about right for when Apple pass will wrap the value onto the line after the label
  if (length > 30) return verticalFieldWithlabel({ key, label, value, isLastField })
  else return horizontalFieldWithlabel({ key, label, value, isLastField })
}

const verticalFieldWithlabel = ({ key, label, value, isLastField }) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="max-w-full">
    <div className="label">
      {label}
    </div>
    <div className="text">
      {value}
    </div>
  </div>
)

const horizontalFieldWithlabel = ({ key, label, value, isLastField }) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="flex flex-wrap justify-between max-w-full">
    <div className="label">
      {label}
    </div>
    <div className="text">
      {value}
    </div>
  </div>
)

const fieldWithoutLabel = ({ key, value, isLastField }) => (
  <div key={key} style={isLastField ? styles.lastChild : styles.child} className="flex flex-wrap justify-between max-w-full">
    <div className="text">
      {value}
    </div>
  </div>
)


function addDataFormatting(type, value, label) {
  switch (type ? type : "text") { // default to text if not specified
    case "text": return { value: formatText(value), label }
    case "phone": return { value: formatPhone(value, label), label: "" }
    case "email": return { value: formatEmail(value, label), label: "" }
    case "www": return { value: formatWWW(value, label), label: "" }
    case "location": return { value: formatLocation(value), label }
    case "calendar": return { value: formatCalendar(value), label }

    case "date": return { value, label }
    case "time": return { value, label }
    case "datetime": return { value, label }
    case "number": return { value, label }
    case "money": return { value, label }
  }
}

const formatPhone = (value, label) => (<a href={`tel:${value}`}>☎️ {label}</a>)
const formatEmail = (value, label) => (<a href={`mailto:${value}`}>📧 {label}</a>)
const formatWWW = (value, label) => (<a href={value} target="_blank" rel="noreferrer">🔗 {label}</a>)
const formatLocation = (value) => (<a href={`http://maps.google.com?near=${value}&ll=${value}`} target="_blank" rel="noreferrer">📍 Open Map</a>)
const formatCalendar = (value) => (<a href={value} target="_blank" rel="noreferrer">📅 Add to Calendar</a>)

const formatText = text => {

  let html1 = cleanHtml(text, ['a', 'br'])  // Strip tags we do not permit
  let html2 = newLinesToBrTags(html1) // Convert /n to <br/>
  let html3 = tabsToNbsp(html2) // Convert /n to <br/>

  // Decided to disable Apple data detections.
  // User has to enter HTML tags for Google, so no benefit to using data detection in Apple
  // Also ensures more consistent passes across the two platforms.

  //  Detect and link phone numbers
  //  let html3 = tagTelephoneNumbers(html2) // Add a tags to telephone numbers

  // Detect and link website links. Supposed to do emails as well, buit does not work...
  //  let html4 = anchorme(html3.replace(/https?:\/\//,'')) // Add a tags to mail, web - remove http/https part

  // Add date detection
  // Add address detection

  return parseHtml(html3 ? html3.toString() : "") // parseHtml allows us to output HMTL tags in React
}


// Removes unwanted HTML tags / attributes
const cleanHtml = (text, allowedTags) =>
  sanitize(text?.toString() ?? "", {
    allowedTags: allowedTags,
    allowedAttributes: {
      'a': ['href', 'target'],
    },
    selfClosing: ['br'],
    disallowedTagsMode: 'discard',
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  })

const newLinesToBrTags = text => text?.replace(/\n/g, "<br/>")
const tabsToNbsp = text => text?.replace(/\t/g, "&nbsp;&nbsp;&nbsp;")

//const tagTelephoneNumbers = text => text.replace(/(\+?[0-9][0-9()\-\s+]{4,20}[0-9])/g, "<a href='tel:$1'>$1</a>")